import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, X, MapPin, CheckCircle, AlertCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import { ProviderProfileFormData, ProviderAvailability, ProviderProfile, LocationDetectionStatus } from '../types';
import { detectLocationFromBrowser, formatCoordinates, validatePostalCode } from '../utils/locationUtils';
import { validateImage, createPreviewUrl, cleanupPreviewUrl } from '../utils/imageUtils';
import { getProviderProfile } from '../services/api';

interface CreateProviderProfileModalProps {
  isOpen: boolean;
  providerId: string;
  onSuccess: () => void;
  onSubmit: (data: ProviderProfileFormData) => Promise<{ success: boolean; error?: string }>;
  mode?: 'create' | 'edit';
  initialData?: ProviderProfile | null;
  onClose?: () => void;
}

const CARE_TYPES = [
  'Domiciliary Care',
  'Respite Care',
  'Dementia Care',
  'Post-Hospital Care',
  'Elderly Care',
  'Palliative Care',
  'Disability Care',
  'Companionship Care',
  'Live-in Care',
  'Night Care',
  'Child Care',
  'Nursing Home Care/ Care Home Stay',
  'Nursing Care',
  'Hospital Stay Companion',
];

const SERVICES = [
  'Elder Care',
  'Home Nursing',
  'Personal Care',
  'Companionship',
  'Medication Management',
  'Meal Preparation',
  'Transportation',
  'Food Shopping',
  'Cooking/Cleaning',
];

const CREDENTIALS = [
  'Nursing Certificate',
  'CPR Certified',
  'First Aid',
  'CQC Registered',
  'NVQ',
  'DBS Checked',
];

const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export const CreateProviderProfileModal: React.FC<CreateProviderProfileModalProps> = ({
  isOpen,
  providerId,
  onSuccess,
  onSubmit,
  mode = 'create',
  initialData = null,
  onClose,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState<ProviderProfileFormData>({
    providerId,
    name: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    latitude: undefined,
    longitude: undefined,
    typeOfCare: [],
    location: '',
    serviceRadiusKm: 0,
    services: [],
    pricing: {
      hourly: 0,
      daily: undefined,
      monthly: undefined,
    },
    availability: {},
    credentials: [],
    avatar: null,
    pictures: [],
  });

  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [locationStatus, setLocationStatus] = useState<LocationDetectionStatus>('idle');
  const [locationMessage, setLocationMessage] = useState('');
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [picturesPreviews, setPicturesPreviews] = useState<string[]>([]);

  useEffect(() => {
    const fetchProviderData = async () => {
      if (mode === 'create' && providerId && isOpen) {
        try {
          const response = await getProviderProfile(providerId);
          if (response.success && response.provider) {
            setFormData(prev => ({
              ...prev,
              name: response.provider.name || '',
              phone: response.provider.phone || '',
            }));
          }
        } catch (error) {
          console.error('Failed to fetch provider data:', error);
        }
      }
    };

    if (mode === 'edit' && initialData) {
      const typeOfCareArray = Array.isArray(initialData.typeOfCare)
        ? initialData.typeOfCare
        : (typeof initialData.typeOfCare === 'string' && initialData.typeOfCare.trim() !== '')
          ? [initialData.typeOfCare]
          : [];

      setFormData({
        providerId: String(initialData.userId),
        name: initialData.name || '',
        phone: initialData.phone || '',
        address: initialData.address || '',
        postalCode: initialData.postalCode || '',
        city: initialData.city || '',
        latitude: initialData.latitude,
        longitude: initialData.longitude,
        typeOfCare: typeOfCareArray,
        location: initialData.location,
        serviceRadiusKm: initialData.serviceRadiusKm,
        services: initialData.services,
        pricing: initialData.pricing,
        availability: initialData.availability || {},
        credentials: initialData.credentials,
        avatar: null,
        pictures: [],
      });

      if (initialData.avatar) {
        setAvatarPreview(initialData.avatar);
      }

      if (initialData.latitude && initialData.longitude) {
        setLocationStatus('success');
        setLocationMessage(`Location detected: ${formatCoordinates(initialData.latitude, initialData.longitude)}`);
      }
    } else {
      fetchProviderData();
    }
  }, [mode, initialData, providerId, isOpen]);

  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith('blob:')) {
        cleanupPreviewUrl(avatarPreview);
      }
      picturesPreviews.forEach(url => {
        if (url.startsWith('blob:')) {
          cleanupPreviewUrl(url);
        }
      });
    };
  }, [avatarPreview, picturesPreviews]);

  const handleDetectLocation = async () => {
    setDetectingLocation(true);
    setLocationStatus('detecting');
    setLocationMessage('Detecting your location from browser...');

    const result = await detectLocationFromBrowser();

    setDetectingLocation(false);

    if (result.success && result.latitude && result.longitude) {
      setFormData(prev => ({
        ...prev,
        latitude: result.latitude,
        longitude: result.longitude,
      }));
      setLocationStatus('success');
      setLocationMessage(`Location detected: ${formatCoordinates(result.latitude, result.longitude)}`);
    } else {
      setLocationStatus('failed');
      setLocationMessage(result.error || 'Failed to detect location. Please enter coordinates manually.');
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImage(file);
    if (!validation.isValid) {
      setStepErrors(prev => ({ ...prev, avatar: validation.error || '' }));
      return;
    }

    setStepErrors(prev => ({ ...prev, avatar: '' }));
    if (avatarPreview && avatarPreview.startsWith('blob:')) {
      cleanupPreviewUrl(avatarPreview);
    }

    setFormData(prev => ({ ...prev, avatar: file }));
    setAvatarPreview(createPreviewUrl(file));
  };

  const handlePicturesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length > 5) {
      setStepErrors(prev => ({ ...prev, pictures: 'Maximum 5 images allowed' }));
      return;
    }

    for (const file of files) {
      const validation = validateImage(file);
      if (!validation.isValid) {
        setStepErrors(prev => ({ ...prev, pictures: validation.error || '' }));
        return;
      }
    }

    setStepErrors(prev => ({ ...prev, pictures: '' }));
    picturesPreviews.forEach(url => cleanupPreviewUrl(url));

    setFormData(prev => ({ ...prev, pictures: files }));
    setPicturesPreviews(files.map(file => createPreviewUrl(file)));
  };

  const removeAvatar = () => {
    if (avatarPreview && avatarPreview.startsWith('blob:')) {
      cleanupPreviewUrl(avatarPreview);
    }
    setFormData(prev => ({ ...prev, avatar: null }));
    setAvatarPreview(null);
  };

  const removePicture = (index: number) => {
    const newPictures = formData.pictures?.filter((_, i) => i !== index) || [];
    const removedPreview = picturesPreviews[index];

    if (removedPreview) cleanupPreviewUrl(removedPreview);

    setFormData(prev => ({ ...prev, pictures: newPictures }));
    setPicturesPreviews(picturesPreviews.filter((_, i) => i !== index));
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.name || formData.name.length < 2) {
          errors.name = 'Name is required (minimum 2 characters)';
        }
        if (!formData.phone || formData.phone.length < 10) {
          errors.phone = 'Valid phone number is required';
        }
        if (!formData.address || formData.address.length < 5) {
          errors.address = 'Address is required (minimum 5 characters)';
        }
        if (!formData.postalCode) {
          errors.postalCode = 'Postal code is required';
        } else if (!validatePostalCode(formData.postalCode)) {
          errors.postalCode = 'Please enter a valid UK postal code';
        }
        if (!formData.city || formData.city.length < 2) {
          errors.city = 'City is required';
        }
        if (!formData.typeOfCare || formData.typeOfCare.length === 0) {
          errors.typeOfCare = 'Please select at least one type of care';
        }
        if (!formData.serviceRadiusKm || formData.serviceRadiusKm < 1 || formData.serviceRadiusKm > 100) {
          errors.serviceRadiusKm = 'Service radius must be between 1 and 100 miles';
        }
        if (formData.latitude === undefined || formData.longitude === undefined) {
          errors.coordinates = 'Please detect location or enter coordinates manually';
        }
        break;

      case 2:
        break;

      case 3:
        if (formData.services.length === 0) {
          errors.services = 'Please select at least one service';
        }
        break;

      case 4:
        if (!formData.pricing.hourly || formData.pricing.hourly <= 0) {
          errors.hourly = 'Hourly rate is required and must be positive';
        }
        if (formData.pricing.daily && formData.pricing.daily <= 0) {
          errors.daily = 'Daily rate must be positive';
        }
        if (formData.pricing.monthly && formData.pricing.monthly <= 0) {
          errors.monthly = 'Monthly rate must be positive';
        }
        break;

      case 5:
        const hasAtLeastOneDay = Object.values(formData.availability).some(day => day && day.trim() !== '');
        if (!hasAtLeastOneDay) {
          errors.availability = 'Please set availability for at least one day';
        }
        break;
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      setError('');
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setLoading(true);
    setError('');

    try {
      const submissionData = {
        ...formData,
        location: formData.city,
      };

      const result = await onSubmit(submissionData);
      if (result.success) {
        const message = mode === 'edit'
          ? 'You have successfully updated the profile'
          : 'You have completed the profile';
        setSuccessMessage(message);
        setShowSuccessModal(true);
      } else {
        setError(result.error || 'Failed to create profile. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    onSuccess();
  };

  const toggleService = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service],
    }));
  };

  const toggleCredential = (credential: string) => {
    setFormData(prev => ({
      ...prev,
      credentials: prev.credentials.includes(credential)
        ? prev.credentials.filter(c => c !== credential)
        : [...prev.credentials, credential],
    }));
  };

  const toggleCareType = (careType: string) => {
    setFormData(prev => ({
      ...prev,
      typeOfCare: prev.typeOfCare.includes(careType)
        ? prev.typeOfCare.filter(c => c !== careType)
        : [...prev.typeOfCare, careType],
    }));
  };

  const updateAvailability = (day: keyof ProviderAvailability, value: string) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: value,
      },
    }));
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (mode === 'edit' && e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="bg-white border-b border-gray-200 p-6 rounded-t-2xl flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'edit' ? 'Edit Provider Profile' : 'Complete Your Provider Profile'}
            </h2>
            {mode === 'edit' && onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      currentStep > step
                        ? 'bg-green-600 text-white'
                        : currentStep === step
                        ? 'bg-green-600 text-white ring-4 ring-green-100'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? <Check className="h-5 w-5" /> : step}
                  </div>
                  <span className="text-xs mt-1 text-gray-600 hidden sm:block">
                    {step === 1 && 'Basic Info'}
                    {step === 2 && 'Images'}
                    {step === 3 && 'Services'}
                    {step === 4 && 'Pricing'}
                    {step === 5 && 'Availability'}
                  </span>
                </div>
                {step < 5 && (
                  <div
                    className={`h-1 flex-1 mx-2 ${
                      currentStep > step ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., John Smith"
                />
                {stepErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g., 07700 900000"
                />
                {stepErrors.phone && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.phone}</p>
                )}
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Location Details
                </h4>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address, building number"
                />
                {stepErrors.address && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.address}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value.toUpperCase() })}
                    placeholder="e.g., SW1A 1AA"
                  />
                  {stepErrors.postalCode && (
                    <p className="text-red-500 text-sm mt-1">{stepErrors.postalCode}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., London"
                  />
                  {stepErrors.city && (
                    <p className="text-red-500 text-sm mt-1">{stepErrors.city}</p>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <Button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={detectingLocation}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {detectingLocation ? 'Detecting...' : 'Get My Current Location'}
                </Button>

                {locationMessage && (
                  <div className={`mt-3 flex items-start gap-2 text-sm ${
                    locationStatus === 'success' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {locationStatus === 'success' ? (
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    ) : locationStatus === 'failed' ? (
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    ) : null}
                    <span>{locationMessage}</span>
                  </div>
                )}

                <p className="text-xs text-blue-600 mt-2">
                  Uses your browser's geolocation to detect your current coordinates
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <h5 className="text-sm font-semibold text-gray-800">Coordinates <span className="text-red-500">*</span></h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Latitude
                    </label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={formData.latitude || ''}
                      onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                      placeholder="e.g., 51.5074"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Longitude
                    </label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={formData.longitude || ''}
                      onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                      placeholder="e.g., -0.1278"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  Enter coordinates in decimal format or use the button above to detect automatically
                </p>
              </div>

              {stepErrors.coordinates && (
                <p className="text-red-500 text-sm">{stepErrors.coordinates}</p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Types of Care You Provide <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">Select all care types you offer</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {CARE_TYPES.map((type) => (
                    <label
                      key={type}
                      className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-green-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.typeOfCare.includes(type)}
                        onChange={() => toggleCareType(type)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
                {formData.typeOfCare.length > 0 && (
                  <p className="text-xs text-green-600 mt-2">
                    {formData.typeOfCare.length} care type{formData.typeOfCare.length > 1 ? 's' : ''} selected
                  </p>
                )}
                {stepErrors.typeOfCare && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.typeOfCare}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Radius (miles) <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  value={formData.serviceRadiusKm || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, serviceRadiusKm: parseInt(e.target.value) || 0 })
                  }
                  placeholder="e.g., 10 miles"
                  min="1"
                  max="100"
                />
                {stepErrors.serviceRadiusKm && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.serviceRadiusKm}</p>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Images</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Profile Picture {mode === 'edit' && '(Replace)'}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors">
                  {avatarPreview ? (
                    <div className="space-y-3">
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="w-32 h-32 rounded-full object-cover mx-auto"
                      />
                      <div className="flex justify-center gap-2">
                        <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-700">
                          Change
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleAvatarChange}
                            className="hidden"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={removeAvatar}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Click to upload profile picture</p>
                      <p className="text-xs text-gray-500">JPEG, PNG, or WebP (max 5MB)</p>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                {stepErrors.avatar && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.avatar}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Gallery Images (up to 5) {mode === 'edit' && '(Replace All)'}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-green-500 transition-colors">
                  {picturesPreviews.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        {picturesPreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`Gallery ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removePicture(index)}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-600 text-center">
                        {picturesPreviews.length} of 5 images selected
                      </p>
                      <label className="block text-center cursor-pointer text-sm text-blue-600 hover:text-blue-700">
                        Replace All
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          multiple
                          onChange={handlePicturesChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="cursor-pointer block text-center">
                      <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Click to upload gallery images</p>
                      <p className="text-xs text-gray-500">JPEG, PNG, or WebP (max 5MB each, up to 5 images)</p>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        multiple
                        onChange={handlePicturesChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                {stepErrors.pictures && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.pictures}</p>
                )}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Services & Credentials</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Services Offered <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SERVICES.map((service) => (
                    <label
                      key={service}
                      className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-green-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.services.includes(service)}
                        onChange={() => toggleService(service)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{service}</span>
                    </label>
                  ))}
                </div>
                {stepErrors.services && (
                  <p className="text-red-500 text-sm mt-2">{stepErrors.services}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credentials
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CREDENTIALS.map((credential) => (
                    <label
                      key={credential}
                      className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-green-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.credentials.includes(credential)}
                        onChange={() => toggleCredential(credential)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="ml-3 text-sm text-gray-700">{credential}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Details</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hourly Rate (£) <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  value={formData.pricing.hourly || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pricing: { ...formData.pricing, hourly: parseFloat(e.target.value) || 0 },
                    })
                  }
                  placeholder="e.g., 300"
                  min="0"
                  step="0.01"
                />
                {stepErrors.hourly && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.hourly}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Rate (£)
                </label>
                <Input
                  type="number"
                  value={formData.pricing.daily || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pricing: {
                        ...formData.pricing,
                        daily: e.target.value ? parseFloat(e.target.value) : undefined,
                      },
                    })
                  }
                  placeholder="e.g., 2000"
                  min="0"
                  step="0.01"
                />
                {stepErrors.daily && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.daily}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Rate (£)
                </label>
                <Input
                  type="number"
                  value={formData.pricing.monthly || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pricing: {
                        ...formData.pricing,
                        monthly: e.target.value ? parseFloat(e.target.value) : undefined,
                      },
                    })
                  }
                  placeholder="e.g., 45000"
                  min="0"
                  step="0.01"
                />
                {stepErrors.monthly && (
                  <p className="text-red-500 text-sm mt-1">{stepErrors.monthly}</p>
                )}
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Availability Schedule</h3>

              <div className="space-y-3">
                {DAYS_OF_WEEK.map((day) => (
                  <div key={day} className="flex items-center gap-4">
                    <label className="w-28 text-sm font-medium text-gray-700 capitalize">
                      {day}
                    </label>
                    <Input
                      type="text"
                      value={formData.availability[day] || ''}
                      onChange={(e) => updateAvailability(day, e.target.value)}
                      placeholder='e.g., 9AM-6PM or "Off" or "24 Hours"'
                      className="flex-1"
                    />
                  </div>
                ))}
              </div>
              {stepErrors.availability && (
                <p className="text-red-500 text-sm mt-2">{stepErrors.availability}</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border-t border-gray-200 p-6 rounded-b-2xl flex-shrink-0">
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            <div className="text-sm text-gray-600">
              Step {currentStep} of 5
            </div>

            {currentStep < 5 ? (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={loading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading
                  ? (mode === 'edit' ? 'Updating Profile...' : 'Creating Profile...')
                  : (mode === 'edit' ? 'Update Profile' : 'Complete Profile')
                }
              </Button>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Success"
        message={successMessage}
        variant="success"
        actionLabel="OK"
      />
    </div>
  );
};
